import { useState } from "react";
import { LifeBuoy } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, DataTable, DemoBadge, AsyncBoundary, RowsSkeleton, ProjectAvatar } from "@/components/common";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const PRIORITY = { high: "text-err", medium: "text-warn", low: "text-muted-foreground" };
const PRIORITY_LABEL = { high: "Alta", medium: "Média", low: "Baixa" };

export default function AdminTickets() {
  const { data, loading, error } = useAsync(() => api.getTickets());
  const [open, setOpen] = useState(null);

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader icon={LifeBuoy} title="Suporte" description="Chamados abertos pelos clientes. Backend de tickets será conectado posteriormente."
        breadcrumb={<><span>Organização</span><span>/</span><span className="text-foreground">Suporte</span></>}
        actions={<DemoBadge className="hidden sm:inline-flex" />} />

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton />}>
        <DataTable
          columns={[
            { key: "id", header: "Chamado", render: (r) => <span className="font-mono text-xs">{r.id}</span> },
            { key: "subject", header: "Assunto", render: (r) => <span className="font-medium text-foreground">{r.subject}</span> },
            { key: "client", header: "Cliente", render: (r) => <div className="flex items-center gap-2"><ProjectAvatar name={r.client} size="sm" />{r.client}</div> },
            { key: "category", header: "Categoria" },
            { key: "priority", header: "Prioridade", render: (r) => <span className={cn("text-xs font-medium", PRIORITY[r.priority])}>● {PRIORITY_LABEL[r.priority]}</span> },
            { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} size="sm" /> },
            { key: "updated", header: "Atualizado" },
          ]}
          rows={data} onRowClick={(r) => setOpen(r)} testid="admin-tickets-table" />
      </AsyncBoundary>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-lg border-line bg-popover">
          {open && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{open.id}</span><StatusBadge status={open.status} size="sm" /></div>
                <DialogTitle className="font-heading">{open.subject}</DialogTitle>
                <DialogDescription>{open.client} · {open.category} · {open.service}</DialogDescription>
              </DialogHeader>
              <div className="max-h-72 space-y-3 overflow-y-auto py-2">
                {open.messages.map((m, i) => (
                  <div key={i} className={cn("flex", m.from === "agent" ? "justify-end" : "justify-start")}>
                    <div className={cn("max-w-[80%] rounded-2xl px-3 py-2 text-sm", m.from === "agent" ? "bg-brand/15 text-foreground" : "border border-line bg-white/[0.02] text-foreground")}>
                      <p className="mb-0.5 text-[11px] text-muted-foreground">{m.author} · {m.at}</p>
                      {m.text}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
